import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";

import * as S from "../styles";
import { STATES } from "../../Cadastro/Steps/Step2/constants";
import { searchAddressByCep } from "../api";
import useFeatureFlag from "../../../hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "../../../constants/FeatureFlag";
import CollapsibleSection from "./CollapsibleSection";

const AddressSection = ({ formik, completed, active, setIsCepValid, isCepValid, step = 3 }) => {
  const { isFeatureFlagEnable: isCepAutofillEnable } = useFeatureFlag(
    FEATURE_FLAGS.KILL_SWITCH.ENABLE_CEP_AUTOFILL
  );
  const err = field => formik.errors[field] && formik.touched[field];
  const [cities, setCities] = useState([]);
  const [loadingCep, setLoadingCep] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [citiesError, setCitiesError] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [foundCEP, setFoundCEP] = useState(false);
  const [foundDistrict, setFoundDistrict] = useState(false);
  const [foundAddress, setFoundAddress] = useState(false);
  const [bypassCep, setBypassCep] = useState(false);
  const lastCepLookupRef = useRef({ cep: "", valid: false });

  const showRest = bypassCep || foundCEP ||
    !!(formik.values.address || formik.values.district || formik.values.state);

  const loadCities = async stateUF => {
    setLoadingCities(true);
    setCitiesError("");
    try {
      const response = await fetch(`/cities/${stateUF}.json`);
      if (!response.ok) throw new Error("Erro ao carregar cidades");
      const cidades = await response.json();
      const sorted = [...cidades.loc_cities].sort((a, b) =>
        a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" })
      );
      setCities(sorted);
    } catch (e) {
      setCities([]);
      setCitiesError("Não foi possível carregar as cidades. Tente novamente.");
      setFoundCEP(false);
    } finally {
      setLoadingCities(false);
    }
  };

  const resetAddressFields = () => {
    formik.setFieldValue("address", "");
    formik.setFieldValue("district", "");
    formik.setFieldValue("state", "");
    formik.setFieldValue("city", "");
    formik.setFieldValue("address_number", "");
    formik.setFieldValue("address_complement", "");
  };

  const resetFoundFlags = () => {
    setFoundCEP(false);
    setFoundDistrict(false);
    setFoundAddress(false);
  };

  const handleCepBlur = async evt => {
    formik.setTouched({ ...formik.touched, postal_code: true });
    if (!isCepAutofillEnable) return;
    const cep = evt.target.value.replace(/\D/g, "");
    if (cep.length !== 8) return;
    // CEP já consultado: não refaz a busca nem reseta número/complemento
    if (cep === lastCepLookupRef.current.cep) {
      setIsCepValid(lastCepLookupRef.current.valid);
      if (lastCepLookupRef.current.valid) {
        formik.setFieldError("postal_code", undefined);
      }
      return;
    }
    setLoadingCep(true);
    try {
      const result = await searchAddressByCep(cep);
      lastCepLookupRef.current = { cep, valid: result.success };
      if (!result.success) {
        setIsCepValid(false);
        formik.setFieldError("postal_code", result.error || "CEP inválido");
        resetAddressFields();
        resetFoundFlags();
        return;
      }
      setIsCepValid(true);
      formik.setFieldError("postal_code", undefined);
      if (result.fallback) {
        toast.info(
          "Estamos com problemas para localizar o CEP, por favor, insira manualmente."
        );
        resetFoundFlags();
        setBypassCep(true);
        return;
      }
      const { address, city, district, state } = result.data;
      const stateOption = STATES.find(s => s.state === state);
      formik.setFieldValue("address", address);
      formik.setFieldValue("district", district);
      formik.setFieldValue("state", stateOption || {});
      formik.setFieldValue("address_number", "");
      formik.setFieldValue("address_complement", "");
      if (stateOption) {
        setSelectedCity(city);
        loadCities(stateOption.state);
        setFoundCEP(true);
        setFoundDistrict(!!district);
        setFoundAddress(!!address);
      } else {
        resetFoundFlags();
      }
    } catch (error) {
      setIsCepValid(false);
      resetAddressFields();
      resetFoundFlags();
      toast.error("Não foi possível consultar o CEP. Preencha manualmente.");
      setBypassCep(true);
    } finally {
      setLoadingCep(false);
    }
  };

  const handleCepChange = evt => {
    if (evt.target.value.length < 9) {
      setFoundCEP(false);
      setFoundAddress(false);
      setFoundDistrict(false);
      setIsCepValid(false);
    }
    const raw = evt.target.value.replace(/\D/g, "").slice(0, 8);
    const formatted = raw.length > 5 ? `${raw.slice(0, 5)}-${raw.slice(5)}` : raw;
    formik.setFieldValue("postal_code", formatted);
    if (formik.errors.postal_code) {
      formik.setFieldError("postal_code", undefined);
    }
  };

  useEffect(() => {
    const found = cities.find(c => c.name === selectedCity);
    if (found?.id) formik.setFieldValue("city", { id: found.id, name: found.name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cities, selectedCity]);

  const { address, address_number, city, state } = formik.values;
  const summary = `${address}, ${address_number} - ${city?.name}/${state?.state}`;

  return (
    <CollapsibleSection
      title="Endereço de cobrança"
      step={step}
      active={active}
      completed={completed}
      summary={summary}
    >
      <S.Grid cols={2}>
        <S.Field style={!showRest ? { gridColumn: "1 / -1" } : undefined}>
          <label>
            CEP <span className="req">*</span>
          </label>
          <S.CepFieldWrap $loading={loadingCep}>
            <input
              type="text"
              name="postal_code"
              placeholder="00000-000"
              className={`form-control ${
                !loadingCep && (err("postal_code") || !isCepValid)
                  ? "is-invalid"
                  : ""
              }`}
              value={formik.values.postal_code}
              onChange={handleCepChange}
              onBlur={handleCepBlur}
              disabled={loadingCep}
              aria-busy={loadingCep}
            />
            {loadingCep && <S.CepSpinner aria-hidden="true" />}
          </S.CepFieldWrap>
          {loadingCep ? (
            <span className="hint">Buscando endereço...</span>
          ) : (
            err("postal_code") && (
              <span className="err">{formik.errors.postal_code}</span>
            )
          )}
          {!showRest && !loadingCep && (
            <S.LinkButton
              type="button"
              onClick={() => setBypassCep(true)}
              style={{ alignSelf: "flex-start", marginTop: 4 }}
            >
              Não sei meu CEP
            </S.LinkButton>
          )}
        </S.Field>

        {showRest && (
        <>
        <S.Field>
          <label>
            Estado <span className="req">*</span>
          </label>
          <Select
            name="state"
            options={STATES}
            onChange={evt => {
              formik.setFieldValue("state", evt);
              formik.setFieldValue("city", "");
              loadCities(evt.state);
            }}
            value={formik.values.state}
            placeholder="Selecione..."
            getOptionLabel={o => o.name}
            getOptionValue={o => o.id}
            isDisabled={loadingCep || foundCEP || loadingCities}
            styles={S.selectStyles}
            onBlur={() => formik.setFieldTouched("state", true)}
          />
        </S.Field>

        <S.Field>
          <label>
            Cidade <span className="req">*</span>
          </label>
          <Select
            name="city"
            options={cities}
            onChange={evt => formik.setFieldValue("city", evt)}
            value={formik.values.city}
            placeholder={
              formik.values.state ? "Selecione..." : "Selecione o estado primeiro"
            }
            getOptionLabel={o => o.name}
            getOptionValue={o => o.id}
            isDisabled={
              loadingCep || loadingCities || !formik.values.state || foundCEP
            }
            styles={S.selectStyles}
            onBlur={() => formik.setFieldTouched("city", true)}
          />
          {citiesError && !loadingCities && (
            <span className="err">{citiesError}</span>
          )}
        </S.Field>

        <S.Field>
          <label>
            Bairro <span className="req">*</span>
          </label>
          <input
            type="text"
            name="district"
            className={`form-control ${err("district") ? "is-invalid" : ""}`}
            value={formik.values.district}
            disabled={loadingCep || foundDistrict}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            maxLength={40}
          />
          {err("district") && (
            <span className="err">{formik.errors.district}</span>
          )}
        </S.Field>

        <S.Field>
          <label>
            Endereço <span className="req">*</span>
          </label>
          <input
            type="text"
            name="address"
            className={`form-control ${err("address") ? "is-invalid" : ""}`}
            value={formik.values.address}
            disabled={loadingCep || foundAddress}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            maxLength={196}
          />
          {err("address") && (
            <span className="err">{formik.errors.address}</span>
          )}
        </S.Field>

        <S.Field>
          <label>
            Número <span className="req">*</span>
          </label>
          <input
            type="text"
            name="address_number"
            className={`form-control ${err("address_number") ? "is-invalid" : ""}`}
            value={formik.values.address_number}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            maxLength={10}
          />
          {err("address_number") && (
            <span className="err">{formik.errors.address_number}</span>
          )}
        </S.Field>

        <S.Field>
          <label>Complemento</label>
          <input
            type="text"
            name="address_complement"
            className="form-control"
            value={formik.values.address_complement}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            maxLength={128}
          />
        </S.Field>
        </>
        )}
      </S.Grid>
    </CollapsibleSection>
  );
};

export default AddressSection;
